/**
 * Simulazione — «cosa succede se cambio il piano».
 *
 * Il what-if, nominato in italiano (scelta di Enrico, 17-08). È voce di barra e
 * non un pulsante dentro il Piano perché è la funzione che chi pianifica userà di
 * più, e da dentro un pulsante non si sarebbe scoperta: la lezione delle Partenze
 * di M3 — *una funzione raggiungibile solo da un drawer, per un utente che non sa
 * che esiste, non esiste*.
 *
 * Due modi di cambiare il piano, la stessa domanda: **il riordino lo propone
 * l'AI**, oppure **lo provi tu**. Stanno insieme perché il pannello di impatto è
 * lo stesso e il confronto prima/dopo si legge nello stesso posto.
 */
import { Check, Info, Loader2, Sparkles } from "lucide-react";
import { useState } from "react";

import { RigaTesta } from "@/components/RigaTesta";
import { useM2Feedback, useM2Ottimizza, useM2Proposta } from "@/hooks/useM2";
import { cn } from "@/lib/utils";
import { ListaPiano } from "@/pages/M2/ListaPiano";
import { fmtMinuti } from "@/pages/M2/format";
import { SetupTag, StampoChip } from "@/pages/M2/shared";
import type { PianoResponse, PropostaLineaDiff } from "@/types/m2";

type Modo = "ai" | "manuale";

export function SimulazioneM2({ piano }: { piano: PianoResponse }) {
  const [modo, setModo] = useState<Modo>("ai");

  return (
    <div className="space-y-4">
      <RigaTesta
        contesto="a parità di quantità e vincoli"
        caricatoIl={piano.kpi.finestra_da}
        etichettaData="sul piano del"
        soloGiorno
      >
        <div className="inline-flex overflow-hidden rounded-lg border text-xs">
          {(
            [
              ["ai", "Riordino dell'AI"],
              ["manuale", "Prova tu"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setModo(k)}
              className={cn(
                "px-3 py-1.5 font-medium",
                modo === k ? "bg-primary text-primary-foreground" : "bg-card hover:bg-accent/40"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </RigaTesta>

      {modo === "ai" ? <RiordinoAI /> : <ProvaTu piano={piano} />}
    </div>
  );
}

// ── Il riordino proposto dall'AI ─────────────────────────────────────

function RiordinoAI() {
  const { data: proposta, isLoading } = useM2Proposta();
  const ottimizza = useM2Ottimizza();
  const feedback = useM2Feedback();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Caricamento…
      </div>
    );
  }

  if (!proposta) {
    return (
      <div className="rounded-lg border bg-card p-5">
        <button
          type="button"
          onClick={() => ottimizza.mutate()}
          disabled={ottimizza.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {ottimizza.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Sparkles className="size-4" />
          )}
          Proponi un riordino
        </button>
        <p className="mt-3 max-w-[68ch] text-sm text-muted-foreground">
          L&apos;euristica riordina l&apos;estrusione <b>raggruppando per stampo</b>, a parità di
          quantità e vincoli, per ridurre i cambi trafila — il vero costo di setup. La ricetta cambia
          quasi sempre ed è inevitabile. Elsa spiega la proposta e <b>l&apos;approvi tu</b>; il
          piano di produzione vero resta quello del MES, che non riscriviamo.
        </p>
      </div>
    );
  }

  const imp = proposta.impatto;
  const applicata = proposta.stato !== "proposta";

  return (
    <div className="space-y-4">
      <div className="flex gap-3 rounded-lg border border-brand-200 bg-accent p-4 dark:border-brand-800">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Sparkles className="size-4" />
        </div>
        <div className="text-sm">
          <div className="font-semibold text-accent-foreground">
            Riordino proposto {proposta.modello ? `· ${proposta.modello}` : "· sintesi automatica"}
          </div>
          <div className="mt-1 whitespace-pre-line text-accent-foreground/90">
            {proposta.spiegazione}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Impatto valore={`−${imp.cambi_stampo_eliminati ?? 0}`} label="Cambi stampo eliminati" buono />
        <Impatto valore={`+${fmtMinuti(imp.minuti_recuperati ?? 0)}`} label="Tempo recuperato" buono />
        <Impatto valore={String(imp.righe_riordinate ?? 0)} label="Lavorazioni riordinate" />
        <Impatto
          valore={String(imp.cambi_ricetta_inevitabili ?? 0)}
          label="Cambi ricetta (inevitabili)"
        />
      </div>

      {proposta.linee.map((l) => (
        <DiffLinea key={l.codice_macchina} linea={l} />
      ))}

      {/*
        ⚠️ «Approva la proposta», non «Applica al piano»: `POST /m2/feedback`
        registra l'approvazione e **non riscrive `m2.piano_riga`** — il piano vero
        vive nel MES e non lo possediamo (doc #1 §8). L'etichetta vecchia prometteva
        una cosa che non avviene, e lo avrebbe promesso anche Elsa quando ha
        guadagnato lo stesso gesto come strumento.
      */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-4 py-3.5">
        <div className="text-sm text-muted-foreground">
          {applicata ? (
            <span className="font-medium text-emerald-700 dark:text-emerald-400">
              Approvazione registrata — il piano del MES non è stato riscritto
            </span>
          ) : (
            "Rivedi la proposta e decidi."
          )}
        </div>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            disabled={feedback.isPending || applicata}
            onClick={() =>
              feedback.mutate({ proposta_id: proposta.proposta_id, outcome: "rifiutata" })
            }
            className="rounded-md border bg-card px-3.5 py-2 text-sm font-medium hover:bg-accent/40 disabled:opacity-50"
          >
            Scarta
          </button>
          <button
            type="button"
            disabled={feedback.isPending || applicata}
            onClick={() =>
              feedback.mutate({ proposta_id: proposta.proposta_id, outcome: "accettata" })
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {feedback.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )}
            Approva la proposta
          </button>
        </div>
      </div>
    </div>
  );
}

function Impatto({ valore, label, buono }: { valore: string; label: string; buono?: boolean }) {
  return (
    <div className="rounded-lg border bg-card p-3.5 text-center">
      <div
        className={cn(
          "text-2xl font-semibold",
          buono ? "text-emerald-700 dark:text-emerald-400" : "text-foreground"
        )}
      >
        {valore}
      </div>
      <div className="mt-0.5 text-[11.5px] text-muted-foreground">{label}</div>
    </div>
  );
}

function DiffLinea({ linea }: { linea: PropostaLineaDiff }) {
  const MAX = 10;
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        {linea.nome ?? `Linea ${linea.codice_macchina}`}
        {linea.is_estrusione && (
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">
            estrusione
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        <DiffColonna titolo="Piano attuale (dal MES)" righe={linea.prima.slice(0, MAX)} />
        <DiffColonna titolo="Proposta (per stampo)" righe={linea.dopo.slice(0, MAX)} after />
      </div>
    </div>
  );
}

function DiffColonna({
  titolo,
  righe,
  after,
}: {
  titolo: string;
  righe: PropostaLineaDiff["prima"];
  after?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <h3
        className={cn(
          "border-b px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wide",
          after ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"
        )}
      >
        {titolo}
      </h3>
      {righe.map((r, i) => (
        <div
          key={`${r.piano_riga_id}-${i}`}
          className={cn(
            "flex items-center gap-2 border-t px-3.5 py-2 text-[12.5px] first:border-t-0",
            after && r.mossa === "spostata" && "bg-brand-50 dark:bg-brand-950/40"
          )}
        >
          <span className="inline-flex size-5 shrink-0 items-center justify-center rounded bg-secondary text-[11px] font-semibold text-muted-foreground">
            {r.sequenza_nuova}
          </span>
          <StampoChip stampo={r.stampo_code} />
          <span className="truncate font-mono text-[11px]">
            {r.semilavorato_codice ?? r.descrizione ?? "—"}
          </span>
          <span className="ml-auto shrink-0">
            <SetupTag tipo={r.setup_tipo} minuti={r.setup_minuti} />
          </span>
        </div>
      ))}
    </div>
  );
}

// ── La prova a mano ──────────────────────────────────────────────────

function ProvaTu({ piano }: { piano: PianoResponse }) {
  return (
    <div className="space-y-3">
      <div className="flex gap-2.5 rounded-lg border border-brand-200 bg-accent px-3.5 py-2.5 text-[12.5px] text-accent-foreground dark:border-brand-800">
        <Info className="size-4 shrink-0 text-primary" />
        <div>
          Il piano su cui provare, in sola lettura. <b>Spostare una lavorazione</b> (su, giù, su
          un&apos;altra macchina) e lo scenario <b>«macchina ferma»</b> con l&apos;impatto sulle
          consegne sono il prossimo passo: servono le date di disponibilità accese, perché senza
          quelle «impatto sulle consegne» non si può calcolare.
        </div>
      </div>
      <ListaPiano linee={piano.linee} />
    </div>
  );
}
